import { forwardRef, ReactNode } from 'react';
import {
  Box,
  Group,
  Stack,
  Text,
  Badge,
  Paper,
  Divider,
  ActionIcon,
  Menu,
  Tooltip,
  Table,
} from '@mantine/core';
import {
  IconDots,
  IconDownload,
  IconPrinter,
  IconMail,
  IconEdit,
  IconTrash,
  IconCopy,
  IconReceipt,
} from '@tabler/icons-react';
import { Card, Button } from '../../ui';
import { FadeIn, HoverLift } from '../../animations';
import { usePreferencesStore } from '../../../stores';
import { LINE_HEIGHT_MAP, LETTER_SPACING_MAP } from '../../../hooks/useAccessibilityStyles';

export interface InvoiceLineItem {
  id: string;
  description: string;
  quantity: number;
  unitPrice: number;
  tax?: number;
  total: number;
}

export interface InvoiceData {
  id: string;
  number: string;
  status: 'draft' | 'sent' | 'viewed' | 'paid' | 'overdue' | 'cancelled';
  date: Date;
  dueDate: Date;
  customer: {
    id: string;
    name: string;
    email?: string;
    address?: string;
  };
  items: InvoiceLineItem[];
  subtotal: number;
  tax: number;
  discount?: number;
  total: number;
  currency?: string;
  notes?: string;
  paidAt?: Date;
  paidAmount?: number;
}

export interface InvoiceCardProps {
  invoice: InvoiceData;
  variant?: 'summary' | 'detailed' | 'compact';
  onView?: () => void;
  onEdit?: () => void;
  onDelete?: () => void;
  onDownload?: () => void;
  onPrint?: () => void;
  onSend?: () => void;
  onDuplicate?: () => void;
  onMarkPaid?: () => void;
  onClick?: () => void;
  className?: string;
}

export const InvoiceCard = forwardRef<HTMLDivElement, InvoiceCardProps>(
  (
    {
      invoice,
      variant = 'summary',
      onView,
      onEdit,
      onDelete,
      onDownload,
      onPrint,
      onSend,
      onDuplicate,
      onMarkPaid,
      onClick,
      className,
    },
    ref
  ) => {
    const lineSpacing = usePreferencesStore((state) => state.lineSpacing);
    const letterSpacingPref = usePreferencesStore((state) => state.letterSpacing);

    const accessibilityStyles = {
      lineHeight: LINE_HEIGHT_MAP[lineSpacing],
      letterSpacing: LETTER_SPACING_MAP[letterSpacingPref],
    };

    const currency = invoice.currency || 'USD';

    const formatCurrency = (amount: number) => {
      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency,
      }).format(amount);
    };

    const formatDate = (date: Date) => {
      return new Date(date).toLocaleDateString(undefined, {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
      });
    };

    const getStatusColor = (status: InvoiceData['status']) => {
      switch (status) {
        case 'paid':
          return 'green';
        case 'sent':
        case 'viewed':
          return 'blue';
        case 'overdue':
          return 'red';
        case 'cancelled':
          return 'gray';
        default:
          return 'yellow';
      }
    };

    const getDaysUntilDue = () => {
      const today = new Date();
      const due = new Date(invoice.dueDate);
      const diff = Math.ceil((due.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
      return diff;
    };

    const renderActionsMenu = () => (
      <Menu position="bottom-end" withinPortal>
        <Menu.Target>
          <ActionIcon variant="subtle" onClick={(e) => e.stopPropagation()}>
            <IconDots size={16} />
          </ActionIcon>
        </Menu.Target>
        <Menu.Dropdown>
          {onDownload && (
            <Menu.Item leftSection={<IconDownload size={14} />} onClick={onDownload}>
              Download PDF
            </Menu.Item>
          )}
          {onPrint && (
            <Menu.Item leftSection={<IconPrinter size={14} />} onClick={onPrint}>
              Print
            </Menu.Item>
          )}
          {onSend && invoice.status === 'draft' && (
            <Menu.Item leftSection={<IconMail size={14} />} onClick={onSend}>
              Send Invoice
            </Menu.Item>
          )}
          {onDuplicate && (
            <Menu.Item leftSection={<IconCopy size={14} />} onClick={onDuplicate}>
              Duplicate
            </Menu.Item>
          )}
          {onMarkPaid && invoice.status !== 'paid' && invoice.status !== 'cancelled' && (
            <Menu.Item leftSection={<IconReceipt size={14} />} onClick={onMarkPaid}>
              Mark as Paid
            </Menu.Item>
          )}
          {(onEdit || onDelete) && <Menu.Divider />}
          {onEdit && invoice.status === 'draft' && (
            <Menu.Item leftSection={<IconEdit size={14} />} onClick={onEdit}>
              Edit
            </Menu.Item>
          )}
          {onDelete && (
            <Menu.Item leftSection={<IconTrash size={14} />} color="red" onClick={onDelete}>
              Delete
            </Menu.Item>
          )}
        </Menu.Dropdown>
      </Menu>
    );

    if (variant === 'compact') {
      return (
        <FadeIn>
          <HoverLift lift={2}>
            <Paper
              ref={ref}
              className={className}
              p="sm"
              withBorder
              style={{ cursor: onClick ? 'pointer' : 'default' }}
              onClick={onClick}
            >
              <Group justify="space-between">
                <Group gap="md">
                  <Box>
                    <Text size="sm" fw={500} style={accessibilityStyles}>{invoice.number}</Text>
                    <Text size="xs" c="dimmed" style={accessibilityStyles}>{invoice.customer.name}</Text>
                  </Box>
                </Group>
                <Group gap="md">
                  <Text fw={600} style={accessibilityStyles}>{formatCurrency(invoice.total)}</Text>
                  <Badge color={getStatusColor(invoice.status)} size="sm">
                    {invoice.status}
                  </Badge>
                  {renderActionsMenu()}
                </Group>
              </Group>
            </Paper>
          </HoverLift>
        </FadeIn>
      );
    }

    if (variant === 'detailed') {
      return (
        <FadeIn>
          <Card ref={ref} className={className}>
            <Stack gap="lg">
              {/* Header */}
              <Group justify="space-between">
                <Box>
                  <Group gap="sm">
                    <Text size="xl" fw={600} style={accessibilityStyles}>Invoice {invoice.number}</Text>
                    <Badge color={getStatusColor(invoice.status)} size="lg">
                      {invoice.status}
                    </Badge>
                  </Group>
                  <Text size="sm" c="dimmed" style={accessibilityStyles}>
                    Issued: {formatDate(invoice.date)} • Due: {formatDate(invoice.dueDate)}
                  </Text>
                </Box>
                <Group gap="xs">
                  {onDownload && (
                    <Button variant="subtle" leftSection={<IconDownload size={16} />} onClick={onDownload}>
                      Download
                    </Button>
                  )}
                  {renderActionsMenu()}
                </Group>
              </Group>

              {/* Customer */}
              <Paper p="md" bg="gray.0" radius="sm">
                <Text size="xs" c="dimmed" mb="xs" style={accessibilityStyles}>Bill To</Text>
                <Text fw={500} style={accessibilityStyles}>{invoice.customer.name}</Text>
                {invoice.customer.email && <Text size="sm" style={accessibilityStyles}>{invoice.customer.email}</Text>}
                {invoice.customer.address && (
                  <Text size="sm" c="dimmed" style={accessibilityStyles}>{invoice.customer.address}</Text>
                )}
              </Paper>

              {/* Line items */}
              <Box>
                <Table>
                  <Table.Thead>
                    <Table.Tr>
                      <Table.Th>Description</Table.Th>
                      <Table.Th style={{ textAlign: 'right' }}>Qty</Table.Th>
                      <Table.Th style={{ textAlign: 'right' }}>Price</Table.Th>
                      <Table.Th style={{ textAlign: 'right' }}>Total</Table.Th>
                    </Table.Tr>
                  </Table.Thead>
                  <Table.Tbody>
                    {invoice.items.map((item) => (
                      <Table.Tr key={item.id}>
                        <Table.Td>{item.description}</Table.Td>
                        <Table.Td style={{ textAlign: 'right' }}>{item.quantity}</Table.Td>
                        <Table.Td style={{ textAlign: 'right' }}>{formatCurrency(item.unitPrice)}</Table.Td>
                        <Table.Td style={{ textAlign: 'right' }}>{formatCurrency(item.total)}</Table.Td>
                      </Table.Tr>
                    ))}
                  </Table.Tbody>
                </Table>
              </Box>

              {/* Totals */}
              <Box style={{ marginLeft: 'auto', width: 250 }}>
                <Group justify="space-between" mb="xs">
                  <Text size="sm" c="dimmed" style={accessibilityStyles}>Subtotal</Text>
                  <Text size="sm" style={accessibilityStyles}>{formatCurrency(invoice.subtotal)}</Text>
                </Group>
                {invoice.discount && (
                  <Group justify="space-between" mb="xs">
                    <Text size="sm" c="dimmed" style={accessibilityStyles}>Discount</Text>
                    <Text size="sm" c="green" style={accessibilityStyles}>-{formatCurrency(invoice.discount)}</Text>
                  </Group>
                )}
                <Group justify="space-between" mb="xs">
                  <Text size="sm" c="dimmed" style={accessibilityStyles}>Tax</Text>
                  <Text size="sm" style={accessibilityStyles}>{formatCurrency(invoice.tax)}</Text>
                </Group>
                <Divider my="xs" />
                <Group justify="space-between">
                  <Text fw={600} style={accessibilityStyles}>Total</Text>
                  <Text size="lg" fw={700} style={accessibilityStyles}>{formatCurrency(invoice.total)}</Text>
                </Group>
                {invoice.paidAmount && invoice.paidAmount < invoice.total && (
                  <>
                    <Group justify="space-between" mt="xs">
                      <Text size="sm" c="dimmed" style={accessibilityStyles}>Paid</Text>
                      <Text size="sm" c="green" style={accessibilityStyles}>{formatCurrency(invoice.paidAmount)}</Text>
                    </Group>
                    <Group justify="space-between">
                      <Text size="sm" fw={500} style={accessibilityStyles}>Balance Due</Text>
                      <Text size="sm" fw={500} c="red" style={accessibilityStyles}>
                        {formatCurrency(invoice.total - invoice.paidAmount)}
                      </Text>
                    </Group>
                  </>
                )}
              </Box>

              {/* Notes */}
              {invoice.notes && (
                <Paper p="sm" bg="gray.0" radius="sm">
                  <Text size="xs" c="dimmed" mb="xs" style={accessibilityStyles}>Notes</Text>
                  <Text size="sm" style={accessibilityStyles}>{invoice.notes}</Text>
                </Paper>
              )}
            </Stack>
          </Card>
        </FadeIn>
      );
    }

    // Summary variant (default)
    const daysUntilDue = getDaysUntilDue();

    return (
      <FadeIn>
        <HoverLift lift={4}>
          <Card
            ref={ref}
            className={className}
            style={{ cursor: onClick ? 'pointer' : 'default' }}
            onClick={onClick}
          >
            <Stack gap="md">
              <Group justify="space-between">
                <Box>
                  <Text fw={600} style={accessibilityStyles}>{invoice.number}</Text>
                  <Text size="sm" c="dimmed" style={accessibilityStyles}>{invoice.customer.name}</Text>
                </Box>
                <Group gap="xs">
                  <Badge color={getStatusColor(invoice.status)}>{invoice.status}</Badge>
                  {renderActionsMenu()}
                </Group>
              </Group>

              <Divider />

              <Group justify="space-between">
                <Box>
                  <Text size="xs" c="dimmed" style={accessibilityStyles}>Amount</Text>
                  <Text size="lg" fw={700} style={accessibilityStyles}>{formatCurrency(invoice.total)}</Text>
                </Box>
                <Box ta="right">
                  <Text size="xs" c="dimmed" style={accessibilityStyles}>Due Date</Text>
                  <Text size="sm" style={accessibilityStyles}>{formatDate(invoice.dueDate)}</Text>
                  {invoice.status !== 'paid' && invoice.status !== 'cancelled' && (
                    <Text
                      size="xs"
                      c={daysUntilDue < 0 ? 'red' : daysUntilDue <= 7 ? 'orange' : 'dimmed'}
                      style={accessibilityStyles}
                    >
                      {daysUntilDue < 0
                        ? `${Math.abs(daysUntilDue)} days overdue`
                        : daysUntilDue === 0
                        ? 'Due today'
                        : `${daysUntilDue} days left`}
                    </Text>
                  )}
                </Box>
              </Group>

              {invoice.status !== 'paid' && onMarkPaid && (
                <Button
                  variant="light"
                  fullWidth
                  onClick={(e) => {
                    e.stopPropagation();
                    onMarkPaid();
                  }}
                >
                  Mark as Paid
                </Button>
              )}
            </Stack>
          </Card>
        </HoverLift>
      </FadeIn>
    );
  }
);

InvoiceCard.displayName = 'InvoiceCard';

export default InvoiceCard;
